/**
 * ASCII-safe city slug subject (almanak-qqq9).
 *
 * City page slugs (e.g. `{city}-festivals-events-2026`, or the Arabic
 * `mahrajanat-faaliyat-{city}-2026`) take their city segment from the
 * per-language `i18n_city_names.slug`. That value is usually romanized
 * ("abha"), but for many cities it is native-script (Arabic, Devanagari,
 * CJK). Native-script city slugs get advertised in the sitemap / hreflang yet
 * 404 at resolution, so we force ASCII from the city's romanized name.
 *
 * Dependency-free so it stays unit-testable without the data layer.
 */

/** True if any character is outside the ASCII range (code point > 127). */
function hasNonAscii(text: string): boolean {
  for (const ch of text) {
    if (ch.codePointAt(0)! > 127) return true;
  }
  return false;
}

/**
 * Lowercase kebab-case, ASCII only. NFD-normalizes and strips combining marks
 * first so accented Latin romanizes (Āgra → agra, Währing → wahring) rather
 * than being dropped (Āgra → gra). Mirrors the slugify in the GeoNames seed
 * script and slug-city.ts, so the romanized subject matches the English slug
 * and non-English city page URLs stay stable. (almanak-knih)
 */
function asciiSlugify(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Returns the per-language slug when it is already ASCII; otherwise romanizes
 * from the fallback (the city's ascii/display name). Returns '' only if the
 * fallback also yields nothing — callers should guard with a stable id.
 */
export function asciiCitySlugSubject(perLangSlug: string, fallbackAscii: string): string {
  if (!hasNonAscii(perLangSlug)) return perLangSlug;
  return asciiSlugify(fallbackAscii);
}
